/* eslint-disable no-param-reassign */
import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import { localize } from '@saviynt/common';
import { Avatar, Icon, MobileDrawer, Typography } from '@saviynt/design-system';
import classnames from 'classnames';
import PropTypes from 'prop-types';

import { getMessageKey } from '../../../utils/utils';

import { KINDS, MIDDLE_ITEM_OFFSET_WIDTH_TO_ADD_ELLIPSES } from './constant';

import './BreadCrumb.css';

function BreadCrumb({ data, onPrevClicked }) {
  const intl = useIntl();
  const { messages } = useSelector((state) => state?.user);

  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);
  const containerRef = useRef(null);
  const levelCount = data?.length || 0;
  const shouldRenderBreadCrumb = Boolean(Array.isArray(data) && levelCount > 1);

  useEffect(() => {
    const addEllipsesToMiddleItem = () => {
      const middleItem = containerRef.current?.getElementsByClassName(
        'BreadCrumb-wrapperMiddleItem'
      )?.[0];

      if (middleItem) {
        if (
          middleItem.offsetWidth <= MIDDLE_ITEM_OFFSET_WIDTH_TO_ADD_ELLIPSES
        ) {
          middleItem.classList.add('active');
        } else {
          middleItem.classList.remove('active');
        }
      }

      let borderItems;

      if (levelCount === 3) {
        borderItems = containerRef.current?.getElementsByClassName(
          'BreadCrumb-wrapperItems--threeNodes'
        );
      }

      if (levelCount === 2) {
        borderItems = containerRef.current?.getElementsByClassName(
          'BreadCrumb-wrapperItems--twoNodes'
        );
      }

      if (borderItems) {
        Object.values(borderItems)?.forEach((borderItem) => {
          if (borderItem.clientWidth <= 80) {
            if (
              borderItem.lastElementChild.offsetWidth <
              borderItem.lastElementChild.scrollWidth
            ) {
              borderItem.style.minWidth = '5rem';
            } else {
              borderItem.style.minWidth = 'fit-content';
            }
          } else {
            borderItem.style.minWidth = '5rem';
          }
        });
      }
    };

    addEllipsesToMiddleItem();

    window.addEventListener('resize', addEllipsesToMiddleItem);

    return () => window.removeEventListener('resize', addEllipsesToMiddleItem);
  }, [data, levelCount]);

  const getIcon = (kind, size = '') => {
    switch (kind) {
      case KINDS.endpoint:
        return (
          <Icon
            kind='Applications'
            color='--color-foreground-secondary-bold'
            size={size === 'medium' ? 'medium' : 'xSmall'}
            className='BreadCrumb-wrapperItemIcons'
          />
        );
      case KINDS.user:
        return (
          <Avatar
            size={size === 'medium' ? 'xSmall' : 'xxSmall'}
            className='BreadCrumb-wrapperItemIcons'
          />
        );
      case KINDS.account:
        return (
          <Icon
            kind='Account'
            color='--color-foreground-secondary-bold'
            size={size === 'medium' ? 'medium' : 'xSmall'}
            className='BreadCrumb-wrapperItemIcons'
          />
        );
      case KINDS.role:
        return (
          <Icon
            kind='Role'
            color='--color-foreground-secondary-bold'
            size={size === 'medium' ? 'medium' : 'xSmall'}
            className='BreadCrumb-wrapperItemIcons'
          />
        );
      case KINDS.entitlement:
        return (
          <Icon
            kind='Entitlement'
            color='--color-foreground-secondary-bold'
            size={size === 'medium' ? 'medium' : 'xSmall'}
            className='BreadCrumb-wrapperItemIcons'
          />
        );
      default:
        return null;
    }
  };

  const handleBreadCrumbItemClick = (level) => {
    const shouldOpenMobileDrawer = Boolean(
      levelCount === 3 &&
        level === 1 &&
        containerRef.current
          ?.getElementsByClassName('BreadCrumb-wrapperMiddleItem')?.[0]
          ?.classList.contains('active')
    );

    if (shouldOpenMobileDrawer) {
      setIsMobileDrawerOpen(true);

      return;
    }

    onPrevClicked(level);
  };

  const handleBreadCrumbMobileItemClick = (level) => {
    if (isMobileDrawerOpen) setIsMobileDrawerOpen(false);

    onPrevClicked(level);
  };

  if (!shouldRenderBreadCrumb) return null;

  return (
    <>
      <div className='BreadCrumb'>
        <div className='BreadCrumb-wrapper' ref={containerRef}>
          {data?.map((item, index, array) => {
            const wrapperItemClasses = classnames(
              'BreadCrumb-wrapperItems',
              levelCount === 2 && 'BreadCrumb-wrapperItems--twoNodes',
              levelCount === 3 && index === 1 && 'BreadCrumb-wrapperMiddleItem',
              levelCount === 3 &&
                index !== 1 &&
                'BreadCrumb-wrapperItems--threeNodes',
              item?.isEndPoint && 'BreadCrumb-wrapperItems--isEndPoint'
            );

            return (
              <React.Fragment key={item.level}>
                <div
                  className={wrapperItemClasses}
                  onClick={
                    item.isActive || item?.isEndPoint
                      ? () => {}
                      : () => handleBreadCrumbItemClick(levelCount - index - 1)
                  }
                  onKeyDown={() => {}}
                  role='button'
                  tabIndex='0'>
                  {array.length - 1 !== index &&
                    getIcon(item?.isEndPoint ? KINDS.endpoint : item.icon)}
                  <Typography
                    kind='body4'
                    desktop='body4-m'
                    mobile='body4-m'
                    className={
                      item.isActive
                        ? 'BreadCrumb-wrapperItemText--active'
                        : 'BreadCrumb-wrapperItemText--inactive'
                    }>
                    {item.text}
                  </Typography>
                </div>
                {array.length - 1 !== index && (
                  <Icon
                    kind='ChevronRight'
                    color='neutral-600'
                    size='xSmall'
                    className='BreadCrumb-wrapperItemIcons'
                  />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
      <MobileDrawer
        isOpen={isMobileDrawerOpen}
        setIsOpen={setIsMobileDrawerOpen}
        isSwipeEnabled
        className='BreadCrumb-drawer'
        content={
          <div className='BreadCrumb-drawerContent'>
            <Typography
              kind='h4'
              desktop='h4-d'
              mobile='h4-d'
              className='BreadCrumb-drawerContentTitle'>
              {messages['SIDEPANEL.breadCrumb.drawer.title.ATTESTATIONS'] ||
                localize(
                  intl,
                  getMessageKey('SIDEPANEL.breadCrumb.drawer.title')
                )}
            </Typography>
            {data?.map((item, index, array) => (
              <div
                className='BreadCrumb-drawerMenuItemWrapper'
                key={item.level}>
                <div
                  className='BreadCrumb-drawerMenuItem'
                  style={{ marginLeft: `${1.25 * index}rem` }}
                  onClick={
                    item.isActive
                      ? () => {}
                      : () =>
                          handleBreadCrumbMobileItemClick(
                            levelCount - index - 1
                          )
                  }
                  onKeyDown={() => {}}
                  role='button'
                  tabIndex='0'>
                  {getIcon(item.icon, 'medium')}
                  <div className='BreadCrumb-drawerMenuItemDetails'>
                    <Typography
                      kind='body2-bold'
                      desktop='body2-bold-d'
                      mobile='body2-bold-d'
                      className={
                        item?.isActive
                          ? 'BreadCrumb-drawerMenuItemTitle--active'
                          : 'BreadCrumb-drawerMenuItemTitle--inactive'
                      }>
                      {item?.text}
                    </Typography>
                    <Typography kind='body3' desktop='body3-d' mobile='body3-d'>
                      {item.icon}
                    </Typography>
                  </div>
                  {array.length - 1 !== index && (
                    <Icon kind='ChevronRight' color='neutral-600' />
                  )}
                </div>
              </div>
            ))}
          </div>
        }
      />
    </>
  );
}

BreadCrumb.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string,
      onClick: PropTypes.func,
      icon: PropTypes.oneOf(Object.values(KINDS)),
      level: PropTypes.number,
      isActive: PropTypes.bool,
    })
  ),
  onPrevClicked: PropTypes.func,
};

BreadCrumb.defaultProps = {
  data: [],
  onPrevClicked: () => {},
};

export default BreadCrumb;
